import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCcw } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Principal-level Error Boundary
 * Captures UI crashes and provides graceful recovery
 */
export class GlobalErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Here we would typically send to a telemetry service like Sentry
    console.error("Uncaught error:", error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/";
  };

  public render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="min-h-[60vh] flex flex-col items-center justify-center p-6 text-center">
          <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mb-6 text-destructive">
            <AlertTriangle className="w-10 h-10" />
          </div>
          <h1 className="text-2xl font-black uppercase tracking-tighter mb-4 italic">
            Ops! Algo não <span className="text-electric">carregou</span> corretamente.
          </h1>
          <p className="text-muted-foreground max-w-md mb-8">
            Nossa equipe de engenharia já foi notificada. Tente atualizar a página ou volte para o início.
          </p>
          <button
            onClick={this.handleReset}
            className="flex items-center gap-2 bg-electric text-white px-8 py-4 rounded-full font-black text-xs uppercase tracking-widest hover:shadow-glow transition-all"
          >
            <RefreshCcw className="w-4 h-4" />
            Recarregar Sistema
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
